/**
 * 充值记录页面
 */

import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Input,
  Select,
  Tag,
  Typography,
  message,
  Tooltip,
  Card,
  Row,
  Col,
  Statistic,
  DatePicker,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  DollarOutlined,
  CrownOutlined,
  GiftOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { Purchase, listPurchases } from '../api/purchases';
import { formatBeijingTimeShort } from '../utils/time';
import dayjs from 'dayjs';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const PurchaseList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchText, setSearchText] = useState('');
  const [productType, setProductType] = useState<string>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [stats, setStats] = useState({
    total_purchases: 0,
    total_subscriptions: 0,
    total_consumables: 0,
  });

  /**
   * 加载充值记录
   */
  const loadPurchases = async () => {
    setLoading(true);
    try {
      const response = await listPurchases({
        page,
        page_size: pageSize,
        product_type: productType || undefined,
        search: searchText || undefined,
        start_date: dateRange?.[0]?.format('YYYY-MM-DD'),
        end_date: dateRange?.[1]?.format('YYYY-MM-DD'),
      });

      if (response.success) {
        setPurchases(response.data.purchases);
        setTotal(response.data.pagination.total);
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('加载充值记录失败:', error);
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  // 初始加载和参数变化时重新加载
  useEffect(() => {
    loadPurchases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, productType]);

  /**
   * 搜索处理
   */
  const handleSearch = () => {
    setPage(1);
    loadPurchases();
  };

  /**
   * 获取产品名称
   */
  const getProductName = (productId: string) => {
    if (productId.includes('weekly')) return '周订阅';
    if (productId.includes('monthly')) return '月订阅';
    if (productId.includes('credit') || productId.includes('pack')) return '积分包';
    return productId;
  };

  /**
   * 表格列定义
   */
  const columns: ColumnsType<Purchase> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '设备 ID',
      dataIndex: 'device_id',
      key: 'device_id',
      width: 180,
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <a onClick={() => navigate(`/users/${text}`)} style={{ fontFamily: 'monospace', fontSize: 12 }}>
            {text.substring(0, 16)}...
          </a>
        </Tooltip>
      ),
    },
    {
      title: '交易 ID',
      dataIndex: 'transaction_id',
      key: 'transaction_id',
      width: 150,
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
            {text.length > 12 ? text.substring(0, 12) + '...' : text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '产品类型',
      dataIndex: 'product_type',
      key: 'product_type',
      width: 100,
      render: (type: string) => (
        <Tag color={type === 'subscription' ? 'blue' : 'green'}>
          {type === 'subscription' ? '订阅' : '消耗品'}
        </Tag>
      ),
    },
    {
      title: '产品',
      dataIndex: 'product_id',
      key: 'product_id',
      width: 120,
      render: (productId: string) => {
        const name = getProductName(productId);
        let icon = <GiftOutlined />;
        let color = 'default';
        
        if (productId.includes('weekly')) {
          icon = <CrownOutlined />;
          color = 'purple';
        } else if (productId.includes('monthly')) {
          icon = <CrownOutlined />;
          color = 'gold';
        }
        
        return (
          <Tag icon={icon} color={color}>
            {name}
          </Tag>
        );
      },
    },
    {
      title: '数量',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'center',
    },
    {
      title: '金额',
      dataIndex: 'price',
      key: 'price',
      width: 100,
      render: (price: string | null) => price || '-',
    },
    {
      title: '购买时间',
      dataIndex: 'purchase_date',
      key: 'purchase_date',
      width: 160,
      render: (time: string) => formatBeijingTimeShort(time),
    },
    {
      title: '过期时间',
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      width: 160,
      render: (time: string | null) => {
        if (!time) return '-';
        const expDate = dayjs(time);
        const isExpired = expDate.isBefore(dayjs());
        return (
          <span style={{ color: isExpired ? 'var(--admin-error)' : undefined }}>
            {formatBeijingTimeShort(time)}
            {isExpired && ' (已过期)'}
          </span>
        );
      },
    },
    {
      title: '记录时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (time: string) => formatBeijingTimeShort(time),
    },
  ];

  return (
    <div>
      {/* 页面标题 */}
      <div style={{ marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          充值记录
        </Title>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="总充值次数"
              value={stats.total_purchases}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="订阅购买"
              value={stats.total_subscriptions}
              prefix={<CrownOutlined />}
              valueStyle={{ color: 'var(--admin-primary)' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="积分包购买"
              value={stats.total_consumables}
              prefix={<GiftOutlined />}
              valueStyle={{ color: 'var(--admin-success)' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 搜索和筛选栏 */}
      <Space style={{ marginBottom: 16, flexWrap: 'wrap' }} size={[8, 8]}>
        <Input
          placeholder="搜索设备ID或交易ID"
          prefix={<SearchOutlined />}
          style={{ minWidth: 200 }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onPressEnter={handleSearch}
          allowClear
        />
        <Select
          placeholder="产品类型"
          style={{ minWidth: 120 }}
          value={productType}
          onChange={setProductType}
          allowClear
        >
          <Select.Option value="subscription">订阅</Select.Option>
          <Select.Option value="consumable">积分包</Select.Option>
        </Select>
        <RangePicker
          value={dateRange}
          onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
          placeholder={['开始日期', '结束日期']}
        />
        <Button icon={<SearchOutlined />} type="primary" onClick={handleSearch}>
          搜索
        </Button>
        <Button icon={<ReloadOutlined />} onClick={loadPurchases}>
          刷新
        </Button>
      </Space>

      {/* 数据表格 */}
      <Table
        columns={columns}
        dataSource={purchases}
        rowKey="id"
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条记录`,
          onChange: (newPage, newPageSize) => {
            setPage(newPage);
            setPageSize(newPageSize);
          },
        }}
        scroll={{ x: 1400 }}
      />
    </div>
  );
};

export default PurchaseList;
